import { useCallback, useState } from "react";

import toast from "react-hot-toast";

import { countries } from "simplydo/core/countries";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import { styled } from "simplydo/react/styled";
import { useTheme } from "simplydo/react/theme";
import {
  Button,
  Card,
  Divider,
  Form,
  type FormInputProps,
  Icon,
  Modal,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";

import { PlaceholderItem, PlaceholderStack } from "components/lib/UI";

const CompanyCardHeader = styled(Card.Header)`
  font-size: 1em;
  color: black;
  font-weight: 600;
`;

const CompanyCardMeta = styled(Card.Meta)`
  font-size: 0.8em;
  color: grey;
`;

type BusinessProfileSearcherProps = {
  forType: string;
  forId: string;
  onSelect: (company: Partial<OpenAPI.Schemas["IdeaBusinessProfile"]>) => void;
  onClose?: () => void;
};

type CreditsafeSearchResult =
  OpenAPI.GET<"/verifications/companies/search">["response"]["companies"][number] & {
    phoneNumbers?: string[];
  };

export const BusinessProfileSearcher = ({
  forType,
  forId,
  onSelect,
  onClose,
}: BusinessProfileSearcherProps) => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);

  const [searchResults, setSearchResults] = useState<CreditsafeSearchResult[]>(
    [],
  );
  const [isSearching, setIsSearching] = useState(false);
  const [country, setCountry] = useState("GB");
  const [companyName, setCompanyName] = useState("");
  const [companyNumber, setCompanyNumber] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Partial<
    OpenAPI.Schemas["IdeaBusinessProfile"]
  > | null>(null);

  const apiSearch = useThrottle(
    (name: string, number: string, countryCode: string) => {
      if ((!name && !number) || (number && number.length <= 1)) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }
      if (number.toString().replace(" ", "").length > 8) {
        toast.error(
          "Company registration is longer than the required maximum length of 8",
        );
        setIsSearching(false);
        return;
      }

      api.verifications.searchForCompanies(
        countryCode,
        number,
        name,
        forType,
        forId,
        (data) => {
          setIsSearching(false);
          setSearchResults(data.companies ?? []);
        },
        (err) => {
          setIsSearching(false);
          toast.error(err.message);
        },
      );
    },
    400,
    [],
  );

  const searchForCreditsafeCompanies = useCallback(
    (name: string, number: string, countryCode: string) => {
      if (!countryCode) {
        return;
      }
      setIsSearching(true);
      apiSearch(name, number, countryCode);
    },
    [apiSearch],
  );

  const onClickCompany = useCallback((company: CreditsafeSearchResult) => {
    setSelectedCompany((prev) =>
      prev?.creditsafeCompanyId === company.id ?
        null
      : {
          name: company.name,
          address: {
            city: company?.address?.city ?? "",
            country: company.country ?? "",
            streetAddress: company.address?.street ?? "",
            postcode: company.address?.postCode ?? "",
          },
          phoneNumber:
            company.phoneNumbers?.length ? company.phoneNumbers[0] : "",
          creditsafeCompanyId: company.id,
          creditsafeRegNo: company.regNo,
          vatNo: company?.vatNo?.length ? company.vatNo[0] : "",
        },
    );
  }, []);

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={true}
    >
      <Modal.Header>Search for your organisation</Modal.Header>
      <Modal.Content scrolling>
        <Form>
          <Form.Field>
            <label htmlFor="organisationName">Organisation name</label>
            <Form.Field
              control={Input}
              required
              id="organisationName"
              placeholder="Type your organisation name.."
              value={companyName}
              loading={isSearching}
              onChange={(
                _e: React.SyntheticEvent,
                { value }: FormInputProps,
              ) => {
                const nextValue = String(value ?? "");
                setCompanyName(nextValue);
                searchForCreditsafeCompanies(nextValue, companyNumber, country);
              }}
            />
          </Form.Field>

          <Form.Field>
            <label htmlFor="registrationCountry">
              Registration country (required)
            </label>
            <Form.Select
              search
              id="registrationCountry"
              value={country}
              onChange={(_e, { value }) => {
                const nextCountry = String(value ?? "");
                setCountry(nextCountry);
                searchForCreditsafeCompanies(
                  companyName,
                  companyNumber,
                  nextCountry,
                );
              }}
              placeholder="Select one"
              options={countries.map((c) => ({
                key: c.iso2,
                flag: c.iso2.toLowerCase(),
                value: c.iso2,
                text: c.name,
              }))}
            />
          </Form.Field>

          <Form.Field>
            <label htmlFor="registrationNumber">Registration number</label>
            <Form.Field
              control={Input}
              id="registrationNumber"
              value={companyNumber}
              loading={isSearching}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setCompanyNumber(e.target.value);
                searchForCreditsafeCompanies(
                  companyName,
                  e.target.value,
                  country,
                );
              }}
              placeholder="12345678"
            />
          </Form.Field>

          <Divider />
          <div>
            {searchResults?.length > 0 ?
              <Card.Group itemsPerRow={theme.sizes.isComputer ? 3 : 1}>
                {searchResults.slice(0, 10).map((company) => {
                  const isSelected =
                    selectedCompany?.creditsafeCompanyId === company.id;

                  return (
                    <Card
                      key={company.id}
                      onClick={() => onClickCompany(company)}
                      style={{ padding: 8 }}
                      fluid
                    >
                      <div
                        style={{
                          display: "flex",
                          flex: 1,
                          flexDirection: "column",
                          justifyContent: "space-between",
                          alignItems: "stretch",
                        }}
                      >
                        <div>
                          <CompanyCardHeader>{company.name}</CompanyCardHeader>
                          <CompanyCardMeta>
                            Reg No: {company.regNo}
                          </CompanyCardMeta>
                          <CompanyCardMeta>{`${company?.address?.city || "City unknown"}, ${company.country || "Country unknown"} `}</CompanyCardMeta>
                        </div>
                        <Button
                          content={
                            isSelected ? "Selected" : "This is my organisation"
                          }
                          icon={isSelected ? "check" : undefined}
                          style={{ marginTop: 5 }}
                          secondary={isSelected}
                        />
                      </div>
                    </Card>
                  );
                })}
              </Card.Group>
            : isSearching ?
              <Card.Group itemsPerRow={theme.sizes.isComputer ? 3 : 1}>
                {Array.from({ length: 6 }).map((_, index) => {
                  return (
                    <Card key={index} style={{ padding: 8 }} fluid>
                      <div
                        style={{
                          display: "flex",
                          flex: 1,
                          flexDirection: "column",
                          justifyContent: "space-between",
                          alignItems: "stretch",
                        }}
                      >
                        <PlaceholderStack gap={8}>
                          <PlaceholderStack gap={4}>
                            <PlaceholderItem height={24} />
                            <PlaceholderItem height={16} />
                            <PlaceholderItem height={16} />
                          </PlaceholderStack>
                          <PlaceholderItem height={48} />
                        </PlaceholderStack>
                      </div>
                    </Card>
                  );
                })}
              </Card.Group>
            : null}
          </div>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button
          content="Cancel"
          onClick={() => {
            onClose?.();
          }}
        />
        <Button
          primary={!selectedCompany}
          onClick={() => {
            onSelect({ name: companyName || "Default business profile" });
          }}
        >
          <Icon name="pencil" />
          Enter details manually
        </Button>
        {selectedCompany ?
          <Button
            loading={loading}
            primary
            onClick={() => {
              setLoading(true);
              const chosenCompany = { ...selectedCompany };

              onSelect(chosenCompany);
              setLoading(false);
            }}
          >
            <Icon name="play" />
            Proceed with {selectedCompany.name}
          </Button>
        : null}
      </Modal.Actions>
    </Modal>
  );
};
